import { InputActionMeta, SelectField, SelectFieldAsyncProps, SelectFieldRef } from '@almbrand/selectfield';
import { ThemeUnion } from '@almbrand/systemicons/dist/cjs/types/icon';
import { ThemeNames } from 'constantsValues';
import { PageContext } from 'context/PageContext';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { FieldValues, UseFormRegister, UseFormSetValue } from 'react-hook-form';
import { useAppSelector } from 'store/hooks';
import {
	getSearchComponent,
	SearchComponent,
	SearchConfiguration,
} from '../../../searchConfiguration/SearchConfiguration';
import { SpdSearch } from '../../../searchConfiguration/SpdSearch';

type SelectActionType =
	| 'clear'
	| 'create-option'
	| 'deselect-option'
	| 'pop-value'
	| 'remove-value'
	| 'select-option'
	| 'set-value';

export interface SearchFieldOption {
	label: string;
	value: string;
}

type SearchFieldComponentProps = SelectFieldAsyncProps<ThemeUnion> & {
	register: UseFormRegister<FieldValues>;
	contentType: string;
	stepContentId: string;
	submitStep?: () => void;
	placeholderText?: string;
	searchConfiguration?: SearchConfiguration;
	secondarySearchConfiguration?: SearchConfiguration;
	stepTitle?: string;
	setValue?: UseFormSetValue<FieldValues>;
	iconPosition?: 'right' | 'left';
	icon?: Icon;
	autocomplete?: React.HTMLInputAutoCompleteAttribute;
};

export const SearchFieldComponent: React.FC<SearchFieldComponentProps> = React.memo(
	({
		register,
		submitStep,
		contentType,
		stepContentId,
		searchConfiguration,
		secondarySearchConfiguration,
		isSearchable = true,
		placeholderText,
		setValue,
		iconPosition = 'right',
		icon,
		autocomplete = 'off',
		...props
	}) => {
		const pageContext = useContext(PageContext);
		const { theme } = pageContext ?? {};
		const themeName = ThemeNames[theme];

		const formState = useAppSelector((state) => state.calculatorForm);

		// Register field with react-hook-form
		const { onChange, name } = register(stepContentId.toString(), { required: true });

		// State for the selected value(s)
		const [searchResultValue, setSearchResultValue] = useState<readonly SearchFieldOption[]>();
		// State for the search component logic
		const [searchComponent, setSearchComponent] = useState<SearchComponent>();
		// State to control rendering after async setup
		const [renderField, setRenderField] = useState(false);
		// State for menu open/close (for async search)
		const [handleMenu, setHandleMenu] = useState<boolean | undefined>(undefined);
		// State for focus
		const [isFocus, setIsFocus] = useState(false);
		// State to track if field has been changed/touched
		const [isTouched, setIsTouched] = useState(false);

		const selectRef = useRef<SelectFieldRef>(null);

		// Set placeholder from prop (fix for CMS naming)
		props.placeHolder = placeholderText;

		// Initialize search component and render flag on mount or when searchConfiguration changes
		useEffect(() => {
			setSearchComponent(getSearchComponent(searchConfiguration, pageContext, secondarySearchConfiguration));
			setRenderField(true);
		}, [searchConfiguration, secondarySearchConfiguration]);

		// // Trigger focus event on input to detect browser autofill

		// Filter options by label or value (case-insensitive)
		const filterValues = useCallback((inputValue: string, options: SearchFieldOption[]): SearchFieldOption[] => {
			const lowerInput = inputValue.toLowerCase();
			return options.filter(
				(option) =>
					option.label.toLowerCase().includes(lowerInput) || option.value.toLowerCase().includes(lowerInput)
			);
		}, []);

		const updateSearchResultValueIfNeeded = (
			filtered: SearchFieldOption[],
			searchResultValue: readonly SearchFieldOption[] | undefined,
			setSearchResultValue: (v: SearchFieldOption[]) => void
		) => {
			if (
				!searchResultValue ||
				searchResultValue.length !== filtered.length ||
				searchResultValue.some((v, i) => v.value !== filtered[i].value || v.label !== filtered[i].label)
			) {
				setSearchResultValue(filtered);
			}
		};

		// Handle value change (select option)
		const handleChange = useCallback(
			async (option: SearchFieldOption, actionTypes?: SelectActionType, focus = true) => {
				if (!searchComponent || !option) return;

				// Handle focus on non safari autofill case
				if (focus) {
					const input = selectRef.current?.getInputElement();

					if (input) {
						// refocus
						setTimeout(() => {
							input.focus();
						}, 100);
					}
				}

				// Prevents firing onChange multiple times if not Safari autofill
				setIsTouched(true);

				const modifiedOption = searchComponent.handleChange
					? await searchComponent.handleChange(option)
					: option;

				// Handle secondary search if configured
				if (searchComponent.secondarySearchConfiguration) {
					const kvhx = await searchComponent.handleSecondaryChange(modifiedOption, register);

					if (
						searchComponent.searchConfiguration &&
						Object.keys(searchComponent.secondarySearchConfiguration).length > 0
					) {
						const staticFieldItems = pageContext?.metaArea?.items?.[0]?.content?.staticFields?.items ?? [];

						const spdBody = {
							name: searchComponent.secondarySearchConfiguration.spdSearchConfigurationName,
							params: (searchComponent.secondarySearchConfiguration.inputFields?.items ?? []).map(
								(item) => {
									const inputUid = item.content.uniqueIdentifier?.uid;
									const matchedDefaultValue = staticFieldItems.find(
										(staticItem) => staticItem?.content?.uniqueIdentifier?.uid === inputUid
									)?.content?.defaultValue;

									return {
										param_name: item.content.param || '',
										param_value: matchedDefaultValue || kvhx || '',
									};
								}
							),
						};

						const spdSearch = new SpdSearch(
							searchComponent.secondarySearchConfiguration,
							pageContext,
							spdBody
						);

						const spdOptions = await spdSearch
							.init()
							//TODO: Remove catch and use real response when BBR is available in test environment
							.catch((error) => {
								const mockBBRResponse = {
									data: {
										URL: '%%sourceurl%%%%myplaceholder%%v1/dynamic-output/group-data/AGT_G_BBR?param1={OFFICIAL_KVHX}&param2={PROD_LINE_ID}&param3={PROD_LINE_VER_NO}',
										name: 'AGT_G_BBR',
										response: {
											AREA_COMMERCIAL: '0',
											CADASTRAL_NO2_BBR: 'BT',
											OUTER_WALL_MATERIAL: '1',
											PRIMARY_HEATING: '1',
											GRADUATION_PRESERV: 'UNK',
											AREA_BASEMENT: '0',
											ROOF_TYPE: '5',
											CADASTRAL_NO1_BBR: '10',
											AREA_GARAGE_BBR: '44',
											KVHX_ABG: '1750563001',
											CADASTRAL_NO3_BBR: 'RØDOVRE BY, RØDOVRE',
											AREA_INDBYGGET_GARAGE: '0',
											YEAR_CONSTRUCTION: '2017',
											AREA_BUILDING: '208',
										},
									},
									successful: true,
								};

								return mockBBRResponse.data.response;
							});

						if (kvhx) {
							const date = new Date();
							const year = date.getFullYear();

							spdOptions['BUILDING_AGE'] = year - Number.parseInt(spdOptions['YEAR_CONSTRUCTION'], 10);
						}

						for (const config of searchComponent.secondarySearchConfiguration.outputFields?.items ?? []) {
							const contentName = config.content.uniqueIdentifier?.contentName;
							const param = config.content.param ?? '';

							const { onChange } = register(contentName, { required: false });
							await onChange({
								target: {
									name: contentName,
									value: {
										id: contentName?.toString(),
										value: spdOptions[param],
										label: param,
										type: contentType,
									},
								},
							});
						}
					}
				}

				// Update form value
				onChange({
					target: {
						name,
						value: {
							id: name,
							label: modifiedOption.label,
							value: modifiedOption.value,
							inputLabel: placeholderText,
							type: contentType,
							stepTitle: props.stepTitle,
						},
					},
				});
				submitStep?.();

				// Update search result value so UI updates with correct value
				setSearchResultValue([option]);
			},
			[searchComponent, register, onChange, name, placeholderText, contentType, props.stepTitle, submitStep]
		);

		// This function loads automatically on render to get async options for SelectField
		const handleAsyncOptions = useCallback(
			async (inputValue: string): Promise<SearchFieldOption[]> => {
				if (!searchComponent) return [];

				const toLovOptions = (result: SearchFieldOption[] | Record<string, unknown> | undefined) =>
					Array.isArray(result) ? result : [];

				const formData = formState[name]?.data;

				// Helper for updating and returning filtered options
				const processOptions = async (options: SearchFieldOption[] | undefined, filterInput: string) => {
					if (!options) return [];
					const filtered = searchComponent.apiSearch ? options : filterValues(filterInput, options);
					updateSearchResultValueIfNeeded(filtered, searchResultValue, setSearchResultValue);
					handleChange(filtered[0]);
					if (searchComponent.apiSearch) {
						return filtered;
					}
					return options;
				};

				// Case 1: Use form value if present and no user input
				if (formData && !inputValue) {
					const options = searchComponent.apiSearch
						? await searchComponent.handleInputChange(formData.label)
						: toLovOptions(await searchComponent.init());
					return processOptions(options, formData.label);
				}

				// Case 1.1: User input present but field not focused (auto fill from browser)
				if (inputValue && !isFocus) {
					const options = await searchComponent.handleInputChange(inputValue);
					setHandleMenu(searchComponent?.apiSearch ? false : undefined);
					processOptions(options, inputValue);
					return options ?? [];
				}

				// Case 2: API search with user input
				if (searchComponent.apiSearch) {
					setSearchResultValue(undefined);
					const options = await searchComponent.handleInputChange(inputValue);
					return options ? options : [];
				}

				// Case 3: Local search with user input
				const options = await searchComponent.init();
				const lovOptions = toLovOptions(options);
				if (lovOptions.length > 0) {
					return filterValues(inputValue, lovOptions);
				}

				return [];
			},
			[searchComponent, filterValues, searchResultValue, handleChange, isFocus]
		);

		const handleInputChange = (inputValue: string, actionMeta?: InputActionMeta) => {
			if (actionMeta?.action === 'input-blur' && actionMeta.prevInputValue && !isTouched)
				fixAutofillOnSafari(actionMeta);

			// Control menu open state for API search
			searchComponent.apiSearch && setHandleMenu(inputValue?.trim().length >= searchComponent.minInputLength);
		};

		const fixAutofillOnSafari = (actionMeta: InputActionMeta) => {
			handleAsyncOptions(actionMeta.prevInputValue).then((options) => {
				options.length > 0 && setSearchResultValue([options[0]]);
				options.length > 0 && handleChange(options[0], null, false);
			});
		};

		const handleFocusChange = () => {
			// Close menu after selection when API search, else keep default
			setHandleMenu(searchComponent?.apiSearch ? false : undefined);

			// Set state isFocus = true;
			setIsFocus(true);
		};

		const getIcon = (): iconProp => {
			if (icon) {
				return {
					iconProp: {
						themeName: themeName,
						icon: icon.icon,
						width: icon.width,
						height: icon.height,
					},
				};
			}
			return {
				iconProp: {
					themeName: themeName,
					icon: 'search',
				},
			};
		};

		return (
			renderField && (
				<SelectField
					{...props}
					isAsync
					icon={getIcon()}
					value={searchResultValue}
					asyncOptions={handleAsyncOptions}
					defaultOptions
					filterOptions={searchComponent?.apiSearch}
					onChange={handleChange}
					onInputChange={handleInputChange}
					onFocus={handleFocusChange}
					errorMessage={props.errorMessage ?? 'Vælg en værdi'}
					iconPosition={iconPosition}
					menuIsOpen={handleMenu}
					onBlur={() => {
						setIsFocus(false);
					}}
					autoComplete={autocomplete}
					ref={selectRef}
				/>
			)
		);
	}
);

SearchFieldComponent.displayName = 'SearchFieldComponent';
